import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "../../../styles/about/components/AboutBriefHistory.module.css";

// Import your church progress images
import img1 from "../../../assets/images/image1.png";
import img2 from "../../../assets/images/image2.png";
import img3 from "../../../assets/images/image3.png";
import img4 from "../../../assets/images/image4.png";
import img5 from "../../../assets/images/image5.png";
import img6 from "../../../assets/images/image6.png";
import img7 from "../../../assets/images/image8.png";
import img8 from "../../../assets/images/image9.png";
import img9 from "../../../assets/images/image10.jpg";
import img10 from "../../../assets/images/image11.jpg";

// Import founder image
import founderImage from "../../../assets/images/father.png";

// Import social media icons
import { 
  FaFacebook, 
  FaTwitter, 
  FaInstagram, 
  FaYoutube, 
  FaLinkedin,
  FaGlobe
} from "react-icons/fa";

const SLIDE_DURATION = 5000;
const RESUME_DELAY = 10000;

const slides = [
  { image: img1, alt: "Church worship gathering" },
  { image: img2, alt: "Ministry outreach program" },
  { image: img3, alt: "Community service event" },
  { image: img4, alt: "Youth ministry activities" },
  { image: img5, alt: "Church building progress" },
  { image: img6, alt: "Leadership conference" },
  { image: img7, alt: "Prayer meeting" },
  { image: img8, alt: "Mission trip" },
  { image: img9, alt: "Church anniversary celebration" },
  { image: img10, alt: "Baptism service" }
];

const socialMediaLinks = [
  {
    name: "Facebook",
    icon: <FaFacebook />,
    url: "https://facebook.com/apostlesebagabo",
    color: "#1877F2"
  },
  {
    name: "Twitter",
    icon: <FaTwitter />,
    url: "https://twitter.com/apostlesebagabo",
    color: "#1DA1F2"
  },
  {
    name: "Instagram",
    icon: <FaInstagram />,
    url: "https://instagram.com/apostlesebagabo",
    color: "#E4405F"
  },
  {
    name: "YouTube",
    icon: <FaYoutube />,
    url: "https://youtube.com/c/apostlesebagabo",
    color: "#FF0000"
  },
  {
    name: "LinkedIn",
    icon: <FaLinkedin />,
    url: "https://linkedin.com/in/apostlesebagabo",
    color: "#0077B5"
  },

];

const TAB_OPTIONS = {
  FOUNDER: 'founder',
  HISTORY: 'history'
};

export default function AboutBriefHistory() {
  const [activeTab, setActiveTab] = useState(TAB_OPTIONS.FOUNDER);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const progressIntervalRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const slideshowRef = useRef(null);

  // Preload images on component mount
  useEffect(() => {
    const preloadImages = async () => {
      const allImages = [...slides, { image: founderImage }];
      const loadPromises = allImages.map((img, index) => {
        return new Promise((resolve) => {
          const image = new Image();
          image.src = img.image;
          image.onload = () => {
            setLoadedImages(prev => ({ ...prev, [index]: true }));
            resolve();
          };
          image.onerror = () => {
            console.warn(`Failed to load image: ${img.image}`);
            resolve();
          };
        });
      });

      try {
        await Promise.all(loadPromises);
      } catch (error) {
        console.error("Error preloading images:", error);
      }
    };

    preloadImages();
  }, []);

  // Auto-advance slideshow (only for history tab)
  const startAutoPlay = useCallback(() => {
    if (activeTab !== TAB_OPTIONS.HISTORY) return;
    
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    progressIntervalRef.current = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, SLIDE_DURATION);
  }, [activeTab, slides.length]);

  const stopAutoPlay = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Handle auto-play state changes
  useEffect(() => {
    if (isPlaying && activeTab === TAB_OPTIONS.HISTORY) {
      startAutoPlay();
    } else {
      stopAutoPlay();
    }

    return () => {
      stopAutoPlay();
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
      }
    };
  }, [isPlaying, activeTab, startAutoPlay, stopAutoPlay]);

  // Handle tab change - start slideshow if switching to history tab
  useEffect(() => {
    if (activeTab === TAB_OPTIONS.HISTORY) {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [activeTab]);

  // Keyboard navigation for slideshow
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeTab !== TAB_OPTIONS.HISTORY) return;
      if (!slideshowRef.current?.contains(e.target)) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          prevSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextSlide();
          break;
        case ' ':
          e.preventDefault();
          togglePlayPause();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeTab]);

  // Slideshow functions
  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
    setIsPlaying(false);
    
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }
    
    resumeTimeoutRef.current = setTimeout(() => {
      setIsPlaying(true);
    }, RESUME_DELAY);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
    setIsPlaying(false);
    
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }
    
    resumeTimeoutRef.current = setTimeout(() => {
      setIsPlaying(true);
    }, RESUME_DELAY);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
    setIsPlaying(false);
    
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }
    
    resumeTimeoutRef.current = setTimeout(() => {
      setIsPlaying(true);
    }, RESUME_DELAY);
  }, [slides.length]);

  const togglePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
    
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }, []);

  const handleImageLoad = useCallback((index) => {
    setLoadedImages(prev => ({ ...prev, [index]: true }));
  }, []);

  // Calculate progress bar width
  const progressWidth = isPlaying 
    ? '100%' 
    : `${((currentSlide + 1) / slides.length) * 100}%`;

  // Handle social media link click
  const handleSocialMediaClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className={styles.briefSection}>
      {/* Section Header */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>BRIEF HISTORY</h2>
        <div className={styles.decorativeLine} />
      </div>

      {/* Tabs Navigation - Founder First */}
      <div className={styles.tabsContainer}>
        <nav className={styles.tabsNav} role="tablist" aria-label="About sections">
          <button
            className={`${styles.tabButton} ${activeTab === TAB_OPTIONS.FOUNDER ? styles.active : ''}`}
            onClick={() => setActiveTab(TAB_OPTIONS.FOUNDER)}
            role="tab"
            aria-selected={activeTab === TAB_OPTIONS.FOUNDER}
            aria-controls="founder-panel"
            id="founder-tab"
          >
            Our Founder
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === TAB_OPTIONS.HISTORY ? styles.active : ''}`}
            onClick={() => setActiveTab(TAB_OPTIONS.HISTORY)}
            role="tab"
            aria-selected={activeTab === TAB_OPTIONS.HISTORY}
            aria-controls="history-panel"
            id="history-tab"
          >
            Our History
          </button>
        </nav>
      </div>

      {/* Tabs Content */}
      <div className={styles.tabsContent}>
        {/* Founder Tab - Now First */}
        <div
          className={`${styles.tabPanel} ${activeTab === TAB_OPTIONS.FOUNDER ? styles.active : ''}`}
          role="tabpanel"
          id="founder-panel"
          aria-labelledby="founder-tab"
          hidden={activeTab !== TAB_OPTIONS.FOUNDER}
        >
          <div className={styles.founderContent}>
            <div className={styles.founderImageContainer}>
              <img 
                src={founderImage}
                alt="Apostle Sebagabo Christophe - Senior Pastor & Founder of Calvary Wide Fellowship Ministries"
                className={`${styles.founderImage} ${loadedImages[slides.length] ? styles.loaded : styles.loading}`}
                loading="lazy"
                onLoad={() => handleImageLoad(slides.length)}
                decoding="async"
              />
              <div className={styles.founderBadge}>
                Apostle & Visionary
              </div>
            </div>

            <div className={styles.founderInfo}>
              <h3 className={styles.founderTitle}>
                Apostle Sebagabo Christophe
              </h3>
              <p className={styles.founderSubtitle}>
                The Visionary & Founder
              </p>
              
              <div className={styles.founderDescription}>
                <p>
Calvary Wide Fellowship Ministries is led by Apostle Christophe Sebagabo, a visionary Christian leader committed to preaching the Gospel of Jesus Christ and raising strong, faith-driven disciples. Through his apostolic leadership, he has guided the growth of a global fellowship, mentoring believers, empowering leaders, and inspiring individuals to live authentic, Christ-centered lives while advancing the Kingdom of God across nations. 
</p>
             </div>

              {/* Social Media Links Section - Replaced the quote */}
              <div className={styles.socialMediaSection}>
                <h4 className={styles.socialMediaTitle}>Connect with Apostle Sebagabo</h4>
                <p className={styles.socialMediaSubtitle}>Follow for updates, teachings, and ministry news</p>
                
                <div className={styles.socialMediaGrid}>
                  {socialMediaLinks.map((platform, index) => (
                    <button
                      key={index}
                      className={styles.socialMediaButton}
                      onClick={() => handleSocialMediaClick(platform.url)}
                      aria-label={`Visit Apostle Sebagabo on ${platform.name}`}
                      style={{ '--platform-color': platform.color }}
                    >
                      <span className={styles.socialMediaIcon} style={{ color: platform.color }}>
                        {platform.icon}
                      </span>
                      <span className={styles.socialMediaName}>
                        {platform.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* History Tab */}
        <div
          className={`${styles.tabPanel} ${activeTab === TAB_OPTIONS.HISTORY ? styles.active : ''}`}
          role="tabpanel"
          id="history-panel"
          aria-labelledby="history-tab"
          hidden={activeTab !== TAB_OPTIONS.HISTORY}
        >
          <div className={styles.historyContent}>
            <div 
              className={styles.slideshowContainer}
              ref={slideshowRef}
              tabIndex={0}
              aria-label="Church history slideshow"
            >
              <div className={styles.slideshow}>
                {slides.map((slide, index) => (
                  <div
                    key={index}
                    className={`${styles.slide} ${index === currentSlide ? styles.active : ''}`}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`Slide ${index + 1} of ${slides.length}`}
                    aria-hidden={index !== currentSlide}
                  >
                    <img 
                      src={slide.image}
                      alt={slide.alt}
                      className={`${styles.slideImage} ${loadedImages[index] ? styles.loaded : styles.loading}`}
                      loading={index <= 1 ? "eager" : "lazy"}
                      onLoad={() => handleImageLoad(index)}
                      decoding="async"
                    />
                  </div>
                ))}
                
                {/* Progress Bar */}
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill}
                    style={{ 
                      width: progressWidth,
                      transitionDuration: isPlaying ? `${SLIDE_DURATION}ms` : '0.1s'
                    }}
                  ></div>
                </div>
              </div>

              {/* Play/Pause Button */}
              <button 
                className={styles.slideshowPause}
                onClick={togglePlayPause}
                aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
                aria-pressed={!isPlaying}
              >
                {isPlaying ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Slide Counter */}
              <div className={styles.slideCounter}>
                {currentSlide + 1} / {slides.length}
              </div>

              {/* Navigation Dots */}
              <div 
                className={styles.slideshowNav}
                role="tablist"
                aria-label="Slide navigation"
              >
                {slides.map((_, index) => (
                  <button
                    key={index}
                    className={`${styles.navDot} ${index === currentSlide ? styles.active : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                    aria-selected={index === currentSlide}
                    role="tab"
                  />
                ))}
              </div>

              {/* Navigation Buttons */}
              <div className={styles.slideshowControls}>
                <button 
                  className={`${styles.navButton} ${styles.prev}`}
                  onClick={prevSlide}
                  aria-label="Previous slide"
                >
                  <svg fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                <button 
                  className={`${styles.navButton} ${styles.next}`}
                  onClick={nextSlide}
                  aria-label="Next slide"
                >
                  <svg fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>

            <div className={styles.historyDescription}>
              <div className={styles.historyHeader}>
                <h3 className={styles.historyTitle}>Calvary Wide Fellowship Ministries</h3>
                <p className={styles.historySubtitle}>The Two-Fold Ministry Vision</p>
              </div>
              
              <div className={styles.historyContentText}>
            <p>
                Calvary Wide Fellowship Ministries (CWFM) was established in 2017 in Rwanda with a strong vision to spread the Gospel of Jesus Christ and build a Christ-centered community rooted in faith, prayer, and spiritual transformation. From its beginnings, the ministry focused on creating a place of revival where believers could grow deeper in their relationship with God through teaching, worship, and fellowship. Over time, CWFM expanded beyond its local roots, developing into a growing network of believers united by a shared commitment to living according to biblical principles and advancing the Kingdom of God.
            </p>
            <p>
              As the ministry continued to grow, it embraced church planting and outreach as key elements of its mission, reaching different regions and countries with the message of salvation. Through conferences, prayer gatherings, and discipleship programs, CWFM has impacted many lives, raising leaders and strengthening believers to actively serve in their communities. Today, the ministry stands as a dynamic and expanding fellowship with a global vision, dedicated to transforming lives, strengthening faith, and spreading the Gospel across nations.
            </p>
 </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}